import { useEffect, useMemo, useState } from 'react';
import { TrashIcon } from '@bizfirst/common-react';
import type { AcceptedCredentialType, CredentialDTO, CredentialFormSchema, CredentialTypeDTO } from '../../types';
import { useCredentialAdminApiClient, useCredentialFormRenderer, useCredentialOAuthConnect } from '../../context/AiAgentAdminApiContext';
import { Modal } from '../Shared/Modal';
import { ConfirmDialog } from '../Shared/ConfirmDialog';

export interface CredentialEditorProps {
  /** Omit to create; pass an existing CredentialID to edit. */
  credentialId?: number;
  /** Pre-selected type code for create mode when the caller already knows it (e.g. type-aware CredentialPicker). */
  defaultCredentialTypeCode?: string;
  /** Types selectable in the type-picker step when defaultCredentialTypeCode/credentialId are both absent. */
  allowedCredentialTypes: CredentialTypeDTO[];
  /** The CredentialPicker's matching AcceptedCredentialType entry for the resolved type, when known
   *  (carries editFormID/viewFormID) — threaded down to a custom CredentialFormRenderer so it can
   *  resolve the right form. Undefined for a search-all/type-agnostic picker. */
  acceptedCredentialType?: AcceptedCredentialType;
  onClose: () => void;
  onSaved: (credential: CredentialDTO) => void;
}

// Same hardcoded defaults flow-studio's CredentialDialog.tsx uses (ExecutionScopeID=1, IsShared=true)
// — not user-configurable in this pass either; ported as-is rather than silently invented anew.
const DEFAULT_EXECUTION_SCOPE_ID = 1;

const EMPTY_SCHEMA: CredentialFormSchema = { controls: [] };

/**
 * Create/edit form for a Credential — adapted from flow-studio-designer's CredentialDialog.tsx.
 * The dynamic per-type field list and the OAuth2 "Connect" control are both pluggable extension
 * points (`useCredentialFormRenderer()`/`useCredentialOAuthConnect()`, resolved from the nearest
 * `<AiAgentAdminApiProvider>`): by default they render plain HTML controls straight off
 * `CredentialFormSchema.controls` (see `defaultCredentialFormRenderer.tsx`) with no OAuth popup
 * (a plain-text note instead) — octopus-admin/did-admin/octopus-a2a all use these defaults
 * unchanged. Flow Studio supplies its own Atlas-Forms-backed implementations instead (see
 * `AtlasCredentialFormAdapter.tsx`) to restore its original dynamic form + OAuth popup behavior.
 */
export function CredentialEditor({ credentialId, defaultCredentialTypeCode, allowedCredentialTypes, acceptedCredentialType, onClose, onSaved }: CredentialEditorProps) {
  const api = useCredentialAdminApiClient();
  const FormRenderer = useCredentialFormRenderer();
  const OAuthConnect = useCredentialOAuthConnect();
  const [step, setStep] = useState<'selectType' | 'form'>(credentialId || defaultCredentialTypeCode ? 'form' : 'selectType');
  const [credentialTypeCode, setCredentialTypeCode] = useState<string | undefined>(defaultCredentialTypeCode);
  const [credentialType, setCredentialType] = useState<CredentialTypeDTO | null>(null);
  const [schema, setSchema] = useState<CredentialFormSchema>(EMPTY_SCHEMA);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(!!credentialId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Set when an existing credential's secret values failed to load/parse (see
  // credentialAdminApiClient.getByIdDecrypted's doc comment for the 2026-09-14 incident). Saving
  // is blocked while this is true so a failed decrypt can never silently overwrite real secrets
  // with the empty fieldValues default.
  const [decryptFailed, setDecryptFailed] = useState(false);

  const resolvedType = useMemo(
    () => credentialType ?? allowedCredentialTypes.find((t) => t.Code === credentialTypeCode) ?? null,
    [credentialType, credentialTypeCode, allowedCredentialTypes]
  );

  const loadSchema = async (typeId: number) => {
    const result = await api.getFormSchema(typeId);
    setSchema(result.data?.schema ?? EMPTY_SCHEMA);
  };

  useEffect(() => {
    if (credentialId) {
      setLoading(true);
      Promise.all([api.getById(credentialId), api.getByIdDecrypted(credentialId)]).then(async ([byId, decrypted]) => {
        if (byId.success && byId.data) {
          setName(byId.data.Name);
          setDescription(byId.data.Description ?? '');
          const type = allowedCredentialTypes.find((t) => t.CredentialTypeID === byId.data!.CredentialTypeID) ?? null;
          setCredentialType(type);
          setCredentialTypeCode(type?.Code);
          await loadSchema(byId.data.CredentialTypeID);
        }
        if (decrypted.success && decrypted.data) {
          // decrypted.data is already the parsed field bag (credentialAdminApiClient.getByIdDecrypted
          // JSON.parses the backend's raw decrypted string) — do NOT index a further `.data` off it.
          setFieldValues(decrypted.data);
        } else {
          // Do not fall back to {}: an empty fieldValues renders identically to "no secrets yet",
          // and hitting Save would overwrite the real encrypted data with blanks (2026-09-14
          // incident). Block saving instead and tell the user to retry.
          setDecryptFailed(true);
          setError('Could not load this credential’s saved values. Editing is disabled to avoid overwriting them — close this dialog and try again.');
        }
        setLoading(false);
      });
      return;
    }
    if (defaultCredentialTypeCode) {
      const type = allowedCredentialTypes.find((t) => t.Code === defaultCredentialTypeCode) ?? null;
      setCredentialType(type);
      if (type) loadSchema(type.CredentialTypeID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentialId]);

  const handlePickType = async (type: CredentialTypeDTO) => {
    setCredentialType(type);
    setCredentialTypeCode(type.Code);
    await loadSchema(type.CredentialTypeID);
    setStep('form');
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Credential name is required.');
      return;
    }
    if (!resolvedType) {
      setError('A credential type must be selected.');
      return;
    }
    if (decryptFailed) {
      setError('Could not load this credential’s saved values earlier — refusing to save to avoid overwriting them. Close this dialog and try again.');
      return;
    }
    setSaving(true);
    setError(null);
    const request = {
      Name: name,
      Description: description,
      CredentialTypeID: resolvedType.CredentialTypeID,
      EncryptedData: JSON.stringify(fieldValues),
      ExecutionScopeID: DEFAULT_EXECUTION_SCOPE_ID,
      IsShared: true,
    };
    const result = credentialId ? await api.update(credentialId, request) : await api.create(request);
    setSaving(false);
    if (result.success && result.data) {
      onSaved(result.data);
    } else {
      setError(result.error?.message ?? 'Failed to save credential.');
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!credentialId) return;
    setDeleting(true);
    const result = await api.deleteById(credentialId);
    setDeleting(false);
    if (result.success) {
      onClose();
    } else {
      setError(result.error?.message ?? 'Failed to delete credential.');
      setConfirmDelete(false);
    }
  };

  if (step === 'selectType') {
    return (
      <Modal isOpen title="Create Credential — Select Type" onClose={onClose} width={480}>
        <div className="aaa-entity-grid">
          {allowedCredentialTypes.map((type) => (
            <button key={type.CredentialTypeID} type="button" className="aaa-entity-card aaa-entity-card-button" onClick={() => handlePickType(type)}>
              <strong>{type.Name}</strong>
              {type.Description && <p>{type.Description}</p>}
            </button>
          ))}
          {allowedCredentialTypes.length === 0 && <div className="aaa-entity-dropdown-empty">No credential types available.</div>}
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen title={credentialId ? `Edit Credential — ${name || '…'}` : `Create Credential — ${resolvedType?.Name ?? ''}`} onClose={onClose} width={640}>
      {loading ? (
        <div className="aaa-editor-loading">Loading…</div>
      ) : (
        <>
          {error && <div className="aaa-editor-error">{error}</div>}
          <div className="aaa-form-grid">
            <div className="aaa-form-group aaa-form-group-full">
              <label>Name *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="aaa-form-group aaa-form-group-full">
              <label>Description</label>
              <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>

            {resolvedType?.IsOAuth && (
              OAuthConnect ? (
                <OAuthConnect
                  credentialType={resolvedType}
                  formValues={fieldValues}
                  onExchanged={(fieldUpdates) => setFieldValues((f) => ({ ...f, ...fieldUpdates }))}
                />
              ) : (
                <div className="aaa-form-group-full aaa-editor-note">
                  This is an OAuth credential type. The interactive "Connect" popup flow is not yet built
                  in this admin UI — paste the token/secret values below directly (see Flow Studio's
                  credential dialog for the guided OAuth flow in the meantime).
                </div>
              )
            )}

            {resolvedType && (
              <FormRenderer
                schema={schema}
                credentialType={resolvedType}
                acceptedType={acceptedCredentialType}
                mode={credentialId ? 'edit' : 'create'}
                values={fieldValues}
                onFieldChange={(fieldId, value) => setFieldValues((f) => ({ ...f, [fieldId]: value }))}
              />
            )}
          </div>

          <div className="aaa-editor-footer">
            <div>
              {credentialId && (
                <button type="button" className="aaa-btn aaa-btn-danger" onClick={() => setConfirmDelete(true)} disabled={saving}>
                  <TrashIcon size={13} /> Delete Credential
                </button>
              )}
            </div>
            <div className="aaa-editor-footer-actions">
              <button type="button" className="aaa-btn aaa-btn-default" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="aaa-btn aaa-btn-primary" onClick={handleSave} disabled={saving || decryptFailed}>
                {saving ? 'Saving…' : credentialId ? 'Save Changes' : 'Create Credential'}
              </button>
            </div>
          </div>
        </>
      )}

      <ConfirmDialog
        isOpen={confirmDelete}
        title="Delete Credential"
        message={`Delete "${name}"? This soft-deletes the credential.`}
        busy={deleting}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setConfirmDelete(false)}
      />
    </Modal>
  );
}
